import React, { useRef } from 'react';

const Pagination = ({ currentPage, totalItems, pageSize, paginate, onPageSizeChange }) => {
    // Add a debounce reference
    const processingChange = useRef(false);
    
    // Calculate total pages based on total items and page size
    const totalPages = Math.ceil(totalItems / pageSize);
    
    // Available page size options
    const pageSizeOptions = [5, 10, 20, 50, 100, 500];
    
    // Current range of items being displayed
    const firstItem = Math.min((currentPage - 1) * pageSize + 1, totalItems);
    const lastItem = Math.min(currentPage * pageSize, totalItems);

    // Function to generate page numbers array with ellipsis for large page counts
    const getPageNumbers = () => {
        const pages = [];
        
        // Always show first page
        pages.push(1);
        
        // Calculate range to show around current page
        let rangeStart = Math.max(2, currentPage - 1);
        let rangeEnd = Math.min(totalPages - 1, currentPage + 1);
        
        // Add ellipsis if needed before range
        if (rangeStart > 2) {
            pages.push('...');
        }
        
        // Add pages in range
        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(i);
        }
        
        // Add ellipsis if needed after range
        if (rangeEnd < totalPages - 1) {
            pages.push('...');
        }
        
        // Always show last page if there's more than one page
        if (totalPages > 1) {
            pages.push(totalPages);
        }
        
        return pages;
    };

    // Debounced page size change handler
    const handlePageSizeChange = (e) => {
        e.preventDefault();
        
        // If we're already processing a change, ignore this request
        if (processingChange.current) return;
        
        const newSize = Number(e.target.value);
        
        // Only proceed if the page size actually changed
        if (newSize !== pageSize) {
            // Set processing flag
            processingChange.current = true;
            
            // Call the handler
            onPageSizeChange(newSize);
            
            // Reset processing flag after a short delay
            setTimeout(() => {
                processingChange.current = false;
            }, 300);
        }
    };

    // Debounced page change handler
    const handlePageChange = (newPage) => {
        // If we're already processing a change, ignore this request
        if (processingChange.current) return;
        
        // Only proceed if the page actually changed
        if (newPage !== currentPage) {
            // Set processing flag
            processingChange.current = true;
            
            // Call the paginate function
            paginate(newPage);
            
            // Reset processing flag after a short delay
            setTimeout(() => {
                processingChange.current = false;
            }, 300);
        }
    };

    return (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow-sm">
            {/* Mobile pagination controls */}
            <div className="flex-1 flex justify-between sm:hidden">
                {/* For "Previous" button (mobile) */}
                <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1 ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                    Précédent
                </button>
                {/* For "Next" button (mobile) */}
                <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                    Suivant
                </button>
            </div>
            
            {/* Desktop pagination controls */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-6">
                    <p className="text-sm text-gray-700">
                        Affichage de <span className="font-medium">{firstItem}</span> à <span className="font-medium">{lastItem}</span> sur <span className="font-medium">{totalItems}</span> commandes
                    </p>
                    
                    {/* Page size selector */}
                    <div className="flex items-center space-x-2">
                        <label htmlFor="pageSize" className="text-sm text-gray-600 whitespace-nowrap">
                            Afficher par page:
                        </label>
                        <div className="relative">
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                className="h-full rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:border-[#174540] focus:ring focus:ring-[#174540] focus:ring-opacity-20 appearance-none"
                            >
                                {pageSizeOptions.map(size => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        {/* Previous page button with fixed SVG */}
                        {/* For "Previous" button (desktop) */}
                        <button
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${
                                currentPage === 1 
                                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            <span className="sr-only">Précédent</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                        </button>
                        
                        {/* Page number buttons with ellipsis for large page counts */}
                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700">
                                    ...
                                </span>
                            ) : (
                                /* For page number buttons */
                                <button
                                    key={`page-${page}`}
                                    onClick={() => handlePageChange(page)}
                                    className={`relative inline-flex items-center px-4 py-2 border ${
                                        currentPage === page
                                            ? 'z-10 bg-[#174540] text-white border-[#174540]'
                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                        
                        {/* Next page button */}
                        {/* For "Next" button (desktop) */}
                        <button
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${
                                currentPage === totalPages 
                                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            <span className="sr-only">Suivant</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default Pagination;